// Spec 078 US5 — a lista de links de uma linha: rótulo, quem gerou, quando, último acesso e
// revogação (ConfirmDialog — revogar é terminal). O token nunca aparece aqui (só na geração).
import * as React from "react";
import { Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify, toast } from "@/components/ui/notify";
import { Pill } from "@/components/ui/pill";
import { useRevokeDisplayToken } from "./hooks";
import type { DisplayTokenSummary } from "@/types/display-tokens.types";

const when = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" });
export const fmtWhen = (iso: string | null): string => (iso ? when.format(new Date(iso)) : "—");

export interface DisplayLinksListProps {
  lineId: string;
  items: DisplayTokenSummary[];
}

export function DisplayLinksList({ lineId, items }: DisplayLinksListProps): React.ReactElement {
  const revoke = useRevokeDisplayToken(lineId);

  async function onRevoke(t: DisplayTokenSummary) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: "Revogar link de exibição",
      description: `A TV que usa "${t.label ?? "este link"}" passa a mostrar "Link desativado" na próxima atualização. Não dá para reativar — será preciso gerar outro.`,
      confirmLabel: "Revogar",
    });
    if (!ok) return;
    await revoke.mutateAsync(t.id);
    toast.success("Link revogado.");
  }

  if (items.length === 0) {
    return (
      <p className="text-sm text-text-tertiary" data-testid="display-links-empty">
        Nenhum link gerado para esta linha.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border-subtle" data-testid="display-links-list">
      {items.map((t) => (
        <li key={t.id} className="flex items-center justify-between gap-3 py-2 text-sm">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium text-text-primary">
                {t.label ?? "Sem rótulo"}
              </span>
              {t.revokedAt ? (
                <Pill tone="danger" withDot>
                  revogado {fmtWhen(t.revokedAt)}
                </Pill>
              ) : (
                <Pill tone="success" withDot>
                  ativo
                </Pill>
              )}
            </div>
            <p className="text-xs text-text-tertiary">
              gerado por {t.createdByName} em {fmtWhen(t.createdAt)} · último acesso{" "}
              {fmtWhen(t.lastAccessAt)}
            </p>
          </div>
          {!t.revokedAt ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void onRevoke(t)}
              disabled={revoke.isPending}
              aria-label={`Revogar ${t.label ?? "link"}`}
            >
              <Ban className="h-4 w-4" aria-hidden />
              Revogar
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
